import { Sweep } from '@/core/sweep';
import Button from '@/presentation/components/elements/Button/Button';
import CollectionImage from '@/presentation/components/elements/CollectionImage';
import EpochStatus from '@/presentation/components/elements/EpochStatus/EpochStatus';
import { ellipsis } from '@/utils/ellipsis';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { QueryStatus } from '@tanstack/react-query';
import { useFormatter, useTranslations } from 'use-intl';
import { Chain, formatEther } from 'viem';
import Tooltip from '@/presentation/components/elements/Tooltip';
import Price from '@/presentation/components/elements/Price';

const PAGE_SIZE = 10;

const sortCollections = (c: Sweep) =>
  c.epoch[
    c.epoch.type === 'NEW_COLLECTION_WAR' ? 'newCollections' : 'collections'
  ].toSorted((a, b) => {
    const bVotes =
      Number(formatEther(BigInt(b.votesFor))) -
      Number(formatEther(BigInt(b?.votesAgainst || 0)));
    const aVotes =
      Number(formatEther(BigInt(a.votesFor))) -
      Number(formatEther(BigInt(a?.votesAgainst || 0)));

    return bVotes - aVotes;
  });

const SweepHistoryTable = ({
  chain,
  sweeps,
  status,
}: {
  chain: Chain;
  sweeps?: Sweep[];
  status: QueryStatus;
}) => {
  const t = useTranslations('History.SweepHistoryTable');
  const [visibleRows, setVisibleRows] = useState(PAGE_SIZE);
  const { number } = useFormatter();
  const { push } = useRouter();

  const visibleSweeps = sweeps?.slice(0, visibleRows);

  return (
    <div className="rounded-xl bg-gray-800 py-4">
      <div className="overflow-x-scroll hide-scroll">
        <table className="w-full ">
          <thead className="text-left text-xs text-gray-200 whitespace-nowrap">
            <tr>
              <th className="pl-8 pr-4 py-2 font-medium">{t('war')}</th>
              <th className="px-4 py-2 font-medium">{t('type')}</th>
              <th className="px-4 py-2 font-medium">{t('size')}</th>
              <th className="px-4 py-2 font-medium">{t('tx')}</th>
              <th className="px-4 py-2 font-medium">{t('result')}</th>
              <th className="px-4 py-2 font-medium">{t('status')}</th>
              <th className="pl-4 pr-8 py-2"></th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {visibleSweeps?.map((c) => (
              <tr
                key={c.id}
                onClick={() => push(`/wars/${c.id}`)}
                className="hover:bg-gray-600 cursor-pointer"
              >
                <td className="pr-4 pl-8 py-2">
                  <Link href={`/wars/${c.id}`}>
                    {'#'}
                    {c.id}
                  </Link>
                </td>
                <td className="px-4 py-2">{t(`types.${c.epoch.type}`)}</td>
                <td className="px-4 py-2">
                  <Price value={BigInt(c.sweepSize)} />
                </td>
                <td className="px-4 py-2">
                  <a
                    href={`${chain.blockExplorers?.default.url}/tx/${c.transaction}`}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="hover:underline"
                    // prevent the row from being clicked
                    onClick={(e) => e.stopPropagation()}
                  >
                    {ellipsis(c.transaction)}
                  </a>
                </td>
                <td className="px-4 py-2">
                  <Tooltip
                    labelClassName="inline-flex cursor-help"
                    label={
                      <div className="flex -space-x-4">
                        {sortCollections(c)
                          // if new collection war, get the winner, otherwise get the winners (above the sample size line)
                          .filter((x, i) =>
                            c.epoch.type === 'NEW_COLLECTION_WAR'
                              ? i === 0
                              : Number(c.epoch?.sampleSize) > i,
                          )
                          .map((w, i, a) => (
                            <div
                              className="flex-none w-12 h-12 bg-brand-pink rounded-lg overflow-hidden inline-flex border-4 border-gray-800 p-0.5"
                              // stack on top of each other visually
                              style={{ zIndex: a.length - i }}
                              key={w.id}
                            >
                              <CollectionImage
                                collection={w.collection.id}
                                diameter={36}
                              />
                            </div>
                          ))}
                      </div>
                    }
                    content={
                      <table>
                        <thead>
                          <tr>
                            <th className="p-2 text-left">{'Collection'}</th>
                            <th className="p-2 text-right">{'Votes'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortCollections(c).map((collection) => {
                            return (
                              <tr key={collection.id}>
                                <td className="p-2">
                                  <div className="flex items-center">
                                    <CollectionImage
                                      collection={collection.collection.id}
                                      diameter={32}
                                      className="w-8 h-8 mr-2 flex-none"
                                    />
                                    <span className="whitespace-nowrap">
                                      {collection.collection.name}
                                    </span>
                                  </div>
                                </td>
                                <td className="p-2 text-right">
                                  {number(
                                    Number(
                                      formatEther(
                                        BigInt(collection.votesFor) -
                                          BigInt(collection?.votesAgainst || 0),
                                      ),
                                    ),
                                    {
                                      maximumFractionDigits: 0,
                                    },
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    }
                  />
                </td>
                <td className="px-4 py-2">
                  <EpochStatus status={c.status} />
                </td>
                <td className="pl-4 py-2">
                  <Link href={`/wars/${c.id}`}>
                    <ChevronRightIcon className="text-white h-4 w-4" />
                  </Link>
                </td>
              </tr>
            ))}
            {status === 'pending' && (
              <tr>
                <td colSpan={7} className="p-6 text-center">
                  <ArrowPathIcon className="animate-spin h-6 w-6 inline-flex text-gray-200" />
                </td>
              </tr>
            )}
          </tbody>
          {(sweeps?.length || 0) > visibleRows && (
            <tfoot>
              <tr>
                <td colSpan={7}>
                  <div className="flex justify-center p-4">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => setVisibleRows((x) => x + PAGE_SIZE)}
                    >
                      {'Load more'}
                    </Button>
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default SweepHistoryTable;
